import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { Injectable, inject } from '@angular/core';

import {
  CurrencyUtils,
  PerformanceAccessoryAnswer,
  Study,
  StudyAhriCertificationType,
  UnitPerformanceAccessoryAnswers,
} from '@mitsubishi/shared/data-access';
import { SpinnerService, StateSpinnerService } from '@mitsubishi/ui/spinner';
import { getStateActionErroredFactory } from '@mitsubishi/utils';

import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, Store } from '@ngxs/store';

import { Selection } from '../../models';
import { ProjectActions } from '../project/project.actions';
import { ProjectSelectors } from '../project/project.selectors';
import { STUDY_READ_SOURCE } from './study-read-source.token';
import { StudyActions } from './study.actions';
import { StudySelectors } from './study.selectors';

@Injectable({
  providedIn: 'root',
})
export class StudyFacade extends StateSpinnerService {
  private onActionErrored = getStateActionErroredFactory();

  /**
   * Where the study reads shared with the new page come from: the NGXS project tree, or
   * `ProjectStudiesStore` once `?studies=none` empties it (P1-CONSUMERS.md D3). Members marked "legacy
   * page only" below keep reading the tree — nothing reachable with the new page on uses them.
   */
  readonly #source = inject(STUDY_READ_SOURCE);

  /** Legacy page only. The project page reads `studySummaries`. */
  studies$: Observable<Study[]> = this.store.select(ProjectSelectors.projectStudies);
  /** The project's permanent studies with their counters, in either flag state. */
  studySummaries = this.#source.studySummaries;
  activeStudy$ = this.#source.activeStudy$;
  /** Legacy page only. */
  activeStudyGoodForSelectionUnits = this.store.selectSignal(StudySelectors.getGoodForSelectionUnits);
  isActiveStudyTemporary = this.#source.isActiveStudyTemporary;
  activeStudyId$ = this.store.select(StudySelectors.getRouteStudyId);
  studyUrl$ = combineLatest([this.store.select(ProjectSelectors.projectId), this.activeStudyId$]).pipe(
    map(([projectId, studyId]) => {
      return ['/', 'project', projectId, 'study', studyId];
    })
  );

  modelsAdditionProgress$ = this.store.select(StudySelectors.modelsAdditionProgress);
  unitId$ = this.store.select(StudySelectors.getRouteUnitId);

  activeUnit$ = this.#source.activeUnit$;
  selectedUnit$ = this.#source.selectedUnit$;

  currentStudyOrganizationId = this.#source.currentStudyOrganizationId;

  studyById$ = (studyId: string) => this.#source.studyById$(studyId);
  externalUnitById$ = (unitId: string, workingConditionId: string) =>
    this.#source.externalUnitById$(unitId, workingConditionId);

  constructor(store: Store, spinnerService: SpinnerService, actions$: Actions) {
    super(store, spinnerService, actions$);

    this.addSpinnerActions([
      ProjectActions.Load,
      StudyActions.AddUnitToQuote,
      StudyActions.AssignPerformanceAccessories,
      StudyActions.AssignMultiplePerformanceAccessories,
      StudyActions.DuplicateUnitAndAssignPerformanceAccessories,
      StudyActions.DuplicateUnitAndAssignMultiplePerformanceAccessories,
      StudyActions.EditUnitConfiguration,
    ]);
    this.addSpinnerActions([StudyActions.ToggleGoodForSelection], true);
  }

  getCurrentProjectId(): string {
    return this.store.selectSnapshot(ProjectSelectors.projectId);
  }

  get activeStudyId() {
    return this.store.selectSnapshot(StudySelectors.getRouteStudyId);
  }

  get activeStudy() {
    return this.#source.activeStudy;
  }

  get isActiveStudyCertified() {
    return this.#source.isActiveStudyCertified;
  }

  /** Legacy page only. */
  get studySubcategories() {
    return this.store.selectSnapshot(StudySelectors.getActiveStudySubcategories);
  }

  get currencySymbol(): string {
    return CurrencyUtils.getCurrencySymbolFromCode(this.activeStudy?.currencyCode);
  }

  getStudyById(studyId: string) {
    return this.#source.getStudyById(studyId);
  }

  getSubcategoryByUnitId(unitId: string) {
    return this.#source.getSubcategoryByUnitId(unitId);
  }

  /** The containing subcategory's id — what most unit-level callers need, and all an external unit's pairing carries. */
  getSubcategoryIdByUnitId(unitId: string) {
    return this.#source.getSubcategoryIdByUnitId(unitId);
  }

  getUnit(unitId: string | undefined) {
    return this.#source.getUnit(unitId);
  }

  getExternalUnit(unitId: string | undefined, workingConditionId: string) {
    return this.#source.getExternalUnit(unitId, workingConditionId);
  }

  // STUDY
  @Dispatch()
  open = (studyId: string) => new StudyActions.Open(studyId);

  @Dispatch()
  create = (certified: boolean, organizationId: number, systemType?: StudyAhriCertificationType) =>
    new StudyActions.Create(certified, organizationId, systemType);

  @Dispatch()
  delete = () => new StudyActions.Delete();

  @Dispatch()
  updateName = (name: string) => new StudyActions.UpdateName(name);

  @Dispatch()
  updateNote = (note: string) => new StudyActions.UpdateNote(note);

  updatePerformanceData = (
    subcategoryId: string,
    workingConditionId: string,
    performanceData: any,
    requestTraceId?: string
  ) => {
    this.store.dispatch(
      new StudyActions.UpdatePerformanceData(subcategoryId, workingConditionId, performanceData, requestTraceId)
    );

    return this.onActionErrored(StudyActions.UpdatePerformanceData);
  };

  createUnit = (modelSearchUnitId: string, requestTraceId?: string) => {
    this.store.dispatch(new StudyActions.CreateUnit(modelSearchUnitId, requestTraceId));

    return this.onActionErrored(StudyActions.CreateUnit);
  };

  @Dispatch()
  deleteUnit = (subcategoryId: string, unitId: string) => new StudyActions.DeleteUnit(subcategoryId, unitId);

  @Dispatch()
  toggleGoodForSelection = (unitId: string, subcategoryId: string, performanceId: string) =>
    new StudyActions.ToggleGoodForSelection(subcategoryId, unitId, performanceId);

  @Dispatch()
  updateWorkingCondition = (name: string, subcategoryId: string, workingConditionId: string) =>
    new StudyActions.UpdateWorkingCondition(name, subcategoryId, workingConditionId);

  createWorkingCondition = (subcategoryId: string, requestTraceId?: string) => {
    this.store.dispatch(new StudyActions.CreateWorkingCondition(subcategoryId, requestTraceId));

    return this.onActionErrored(StudyActions.CreateWorkingCondition);
  };

  @Dispatch()
  deleteWorkingCondition = (subcategoryId: string, workingConditionId: string) =>
    new StudyActions.DeleteWorkingCondition(subcategoryId, workingConditionId);

  @Dispatch()
  addUnitToQuote = (selection: Selection, unitUuid: string, performanceId: string) =>
    new StudyActions.AddUnitToQuote(selection, unitUuid, performanceId);

  @Dispatch()
  updateStudyUnitNote = (subcategoryId: string, unitUuid: string, performanceId: string, note: string) =>
    new StudyActions.UpdateStudyUnitNote(subcategoryId, unitUuid, performanceId, note);

  @Dispatch()
  updateAddBleenGroupProgress = (bleenGroupId: string, progress: number) =>
    new StudyActions.UpdateAddBleenGroupProgress(bleenGroupId, progress);

  updateCurrencyCode = (currencyCode: string, requestTraceId?: string) => {
    this.store.dispatch(new StudyActions.UpdateCurrencyCode(currencyCode, requestTraceId));
    return this.onActionErrored(StudyActions.UpdateCurrencyCode);
  };

  @Dispatch()
  clearState = () => new StudyActions.ClearState();

  @Dispatch()
  deleteSubcategory = (subcategoryId: string) => new StudyActions.DeleteSubcategory(subcategoryId);

  @Dispatch()
  downloadPerformanceRequestLog = (workingConditionId: string, unitId: string) =>
    new StudyActions.DownloadPerformanceRequestLog(workingConditionId, unitId);

  @Dispatch()
  assignPerformanceAccessories = (
    performanceId: string,
    unitId: string,
    isTemporary: boolean,
    answers: PerformanceAccessoryAnswer[]
  ) => new StudyActions.AssignPerformanceAccessories(performanceId, unitId, isTemporary, answers);

  assignMultiplePerformanceAccessories = (
    unitAnswers: UnitPerformanceAccessoryAnswers[],
    isTemporary: boolean,
    requestTraceId?: string
  ) => {
    this.store.dispatch(
      new StudyActions.AssignMultiplePerformanceAccessories(isTemporary, unitAnswers, requestTraceId)
    );
    return this.onActionErrored(StudyActions.AssignMultiplePerformanceAccessories);
  };

  assignUnitAndAssignPerformanceAccessories = (
    performanceId: string,
    unitId: string,
    isTemporary: boolean,
    answers: PerformanceAccessoryAnswer[],
    requestTraceId?: string
  ) => {
    this.store.dispatch(
      new StudyActions.DuplicateUnitAndAssignPerformanceAccessories(
        performanceId,
        unitId,
        isTemporary,
        answers,
        requestTraceId
      )
    );
    return this.onActionErrored(StudyActions.DuplicateUnitAndAssignPerformanceAccessories);
  };

  assignUnitAndAssignMultiplePerformanceAccessories = (
    unitAnswers: UnitPerformanceAccessoryAnswers[],
    isTemporary: boolean,
    requestTraceId?: string
  ) => {
    this.store.dispatch(
      new StudyActions.DuplicateUnitAndAssignMultiplePerformanceAccessories(unitAnswers, isTemporary, requestTraceId)
    );
    return this.onActionErrored(StudyActions.DuplicateUnitAndAssignMultiplePerformanceAccessories);
  };

  @Dispatch()
  editUnitConfiguration = (
    studyId: string,
    unitId: string,
    performanceId: string,
    workingConditionId: string,
    fromStudy: boolean
  ) => new StudyActions.EditUnitConfiguration(studyId, unitId, performanceId, workingConditionId, fromStudy);

  addExternalUnit = (
    modelId: string,
    quantity: number,
    performanceId: string,
    workingConditions: any,
    requestTraceId?: string
  ) => {
    this.store.dispatch(
      new StudyActions.AddExternalUnit(modelId, quantity, performanceId, workingConditions, requestTraceId)
    );

    return this.onActionErrored(StudyActions.AddExternalUnit);
  };

  upgradeModels = (requestTraceId?: string) => {
    this.store.dispatch(new StudyActions.UpgradeModels(requestTraceId));

    return this.onActionErrored(StudyActions.UpgradeModels);
  };
}
